import React, { useState } from "react";
import { HorizontalStack, VerticalStack } from "../../../components/Stack";
import { Stack, Text } from "@fluentui/react";
import { colorPallete } from "../../../assets/color";
import LibraryIcon from "../../../assets/Icons/svg/libraryIcon";
import SendIcon from "../../../assets/Icons/svg/sendIcon";
import ReceivedIcon from "../../../assets/Icons/svg/receivedIcon";
import LockIcon from "../../../assets/Icons/svg/lockIcon";
import EditIcon from "../../../assets/Icons/svg/editIcon";
import DocumentIcon from "../../../assets/Icons/svg/documentIcon";
import RedLineDocumentIcon from "../../../assets/Icons/svg/redLineDocument";
import TrashIcon from "../../../assets/Icons/svg/trashIcon";
import { motion } from "framer-motion";
import { getFormattedDateTime, getSplitDocumentName } from "../../../utils";
import { Document } from "../../../models";
import { t } from "i18next";
import OpenFile from "../../../navigation/NoobOrPro";

const styles = {
  container: {
    width: "100%",
    height: "100%",
    padding: "1%",
    gap: "12px",
  },
  card: {
    width: "100%",
    height: "64px",
    padding: "12px",
    gap: "12px",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
    borderRadius: "8px"
  },
  fileName: {
    fontFamily: "Poppins",
    fontSize: "14px",
    lineHeight: "20px",
    fontWeight: 500,
    color: "#323232",
  },
  versions: {
    fontFamily: "Poppins",
    fontSize: "12px",
    lineHeight: "16px",
    fontWeight: 400,
    color: "#717070",
  },
  dateTime: {
    fontFamily: "Poppins",
    fontSize: "14px",
    lineHeight: "20px",
    fontWeight: 400,
    color: "#717070",
  },
};

const DocumentListView = (props: any) => {
  const [hoverId, setHoverId] = useState<number | undefined>(0);
  const { deleteDocName, showModal, deleteRootDocId } = props;

  function getFileTypeIcon(fileType?: string, isRedlined?: boolean) {
    if (fileType === "sent") {
      return <SendIcon />;
    } else if (fileType === "received") {
      return <ReceivedIcon />;
    } else if (fileType === "draft" && isRedlined === false) {
      return <DocumentIcon />;
    } else if (fileType === "draft" && isRedlined === true) {
      return <RedLineDocumentIcon />;
    }
  }

  function getFileTypeDate(fileType?: string) {
    if (fileType === "sent") {
      return (
        <Text
          style={{
            fontFamily: "Poppins",
            fontSize: "14px",
            lineHeight: "20px",
            fontWeight: 400,
            color: "#717070",
          }}
        >
          {t("sent_on")}
        </Text>
      );
    } else if (fileType === "received") {
      return (
        <Text
          style={{
            fontFamily: "Poppins",
            fontSize: "14px",
            lineHeight: "20px",
            fontWeight: 400,
            color: "#717070",
          }}
        >
          {t("received_on")}
        </Text>
      );
    } else {
      return (
        <Text
          style={{
            fontFamily: "Poppins",
            fontSize: "14px",
            lineHeight: "20px",
            fontWeight: 400,
            color: "#717070",
          }}
        >
          {t("saved_on")}
        </Text>
      );
    }
  }

  function getIcons(version: Document) {
    return (
      <HorizontalStack
        style={{
          width: "auto",
          height: "auto",
          gap: "3px",
          justifyContent: "flex-end",
        }}
      >
        {version.fileType === "sent" && <div className="whiteIconHover"><LockIcon /></div>}
        <Stack
          onClick={() => {
            version.metadata.fileType === "pdf"
            ? window.open(version.metadata.documentLink) :
           
            OpenFile(version.metadata.documentLink, version.metadata.documentWebLink);
          }}
        >
          <div className="whiteIconHover">
            <EditIcon />
          </div>
        </Stack>
        <Stack
          style={{ cursor: "pointer" }}
          onClick={() => {
            deleteDocName(version.rootDocumentName);
            deleteRootDocId(version.rootDocumentId);
            showModal();
          }}
        >
          <div className="whiteIconHover"><TrashIcon /></div>
        </Stack>
      </HorizontalStack>
    );
  }

  const ListCard = (props: any) => {
    const doc: Document = props.document;
    // DEX-573 M4: read the latest version + count straight from the /root row
    // (M3 enrichment) instead of a per-document /versions call. Fall back to the
    // doc itself for version-item shapes that carry version fields inline.
    const latest: Document | undefined = doc?.latestVersion ?? doc;
    const versionCount: number =
      doc?.versionCount ?? doc?.versions?.length ?? (latest ? 1 : 0);
    return (
      <HorizontalStack style={styles.card}>
        <LibraryIcon />
        <HorizontalStack
          style={{
            width: "48%",
            paddingLeft: "12px",
            gap: "12px",
            alignItems: "center",
          }}
          title={getSplitDocumentName(doc?.rootDocumentName)}
        >
          <Text
            style={{
              ...styles.fileName,
              whiteSpace: "nowrap",
              textOverflow: "ellipsis",
              overflow: "hidden",
              width:"72%"
            }}
          >
            {getSplitDocumentName(doc?.rootDocumentName) ?? ""}
          </Text>
          <HorizontalStack
            style={{
              height: "24px",
              borderRadius: "4px",
              border: "1px solid #E8ECF4 ",
              padding: "4px 8px",
              backgroundColor: "#F8F8F8",
              alignItems: "center",
            }}
          >
            <Text style={styles.versions}>
              {versionCount}&nbsp;
            </Text>
            <Text style={styles.versions}>
              {versionCount === 1
                ? t("version")
                : t("versions")}
            </Text>
          </HorizontalStack>
        </HorizontalStack>
        {latest && (
          <HorizontalStack
            horizontalAlign="start"
            style={{
              width: "50%",
              gap: "30px",
              alignItems: "center",
              paddingLeft: "5px",
              justifyContent: "space-between",
            }}
          >
            <HorizontalStack
              style={{ width: "158px", gap: "4px", alignItems: "center" }}
            >
              {getFileTypeIcon(latest?.fileType, latest?.metadata?.isRedlined)}
              <Text
                style={{
                  fontFamily: "Poppins",
                  fontSize: "14px",
                  lineHeight: "20px",
                  fontWeight: 600,
                  color: "#323232",
                }}
              >
                V{latest.version}
              </Text>
              <Text
                style={{
                  fontFamily: "Poppins",
                  fontSize: "14px",
                  lineHeight: "20px",
                  fontWeight: 500,
                  color: "#717070",
                }}
              >
                {t("most_recent")}
              </Text>
            </HorizontalStack>
            <HorizontalStack style={styles.dateTime}>
              {getFileTypeDate(latest?.fileType)}&nbsp;
              {getFormattedDateTime(String(latest?.lastUpdatedDate))[0]}
              &nbsp;|&nbsp;
              {getFormattedDateTime(String(latest?.lastUpdatedDate))[1]}
            </HorizontalStack>
            {getIcons(latest)}
          </HorizontalStack>
        )}
      </HorizontalStack>
    );
  };

  const docList = (doc: Document) => {
    return (
      <motion.div
        animate={{
          opacity: 1,
          border:
            props.selectedId === doc.rootDocumentId
              ? "1px solid #323232"
              : hoverId === doc.rootDocumentId
              ? "1px solid #323232"
              : "1px solid #E8ECF4",
        }}
        initial={{
          opacity: 0,
        }}
        style={{
          cursor: "pointer",
          borderRadius: "8px",
          backgroundColor:
            props.selectedId === doc.rootDocumentId ? "#F3F5F9" : "#FFFFFF",
        }}
        onMouseEnter={() => setHoverId(doc.rootDocumentId)}
        onMouseLeave={() => {
          setHoverId(0);
        }}
        onClick={() => {
          props.handleFilePath(doc.rootDocumentId);
          props.setSelectedDocument(doc.rootDocumentName);
          props.setSelectedId(doc.rootDocumentId);
        }}
        key={doc.documentId}
      >
        <ListCard document={doc} />
      </motion.div>
    );
  };
  return (
    <VerticalStack
      style={{
        ...styles.container,
        overflowY: props.listView ? "visible" : "scroll",
        height: props.listView ? "" : "351px",
        backgroundColor: "",
      }}
    >
      {props.data &&
        props.data?.length !== 0 &&
        [...props.data]
          .sort((a: Document, b: Document) => {
            const dateA = a.lastActivityDate ? new Date(a.lastActivityDate).getTime() : 0;
            const dateB = b.lastActivityDate ? new Date(b.lastActivityDate).getTime() : 0;
            return dateB - dateA;
          })
          .map((doc: Document) => {
            return docList(doc);
          })}
    </VerticalStack>
  );
};

export default DocumentListView;
